import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import get from "lodash/get";
import { type IdTokenClaims, User } from "oidc-client-ts";
import { useCallback } from "react";
import useCurrentUserManager from "@/hooks/use-current-user-manager";
import useHandleApiError from "@/hooks/use-handle-api-error";
import { useLocalizedRouter } from "@/hooks/use-localized-router";
import AuthRepository from "@/repositories/auth/auth-repository";
import PeopleRepository from "@/repositories/people/people-repository";
import { peopleRepositoryV2 } from "@/repositories/people/people-v2";
import type { Person } from "@/repositories/people/types";
import usePersistedBoundStore from "@/store/persisted-bound-store";
import { isAuthenticatedWithIdentification } from "@/utils/auth";
import { syncTokensToMiddleware } from "@/utils/auth-cookies";
import { saveTabSession } from "@/utils/tab-session";
import { useSilentTokenRefresh } from "./use-silent-token-refresh";

const authRepository = new AuthRepository();
const peopleRepository = PeopleRepository.getInstance();

export default function useControlledBasicProcessLogin() {
  const currentUserManager = useCurrentUserManager();
  const environment = usePersistedBoundStore.use.environment();
  const environmentUrl = usePersistedBoundStore.use.environmentUrl();
  const handleApiError = useHandleApiError();
  const silentRefresh = useSilentTokenRefresh();
  const realm = usePersistedBoundStore.use.realm();
  const { navigateTo } = useLocalizedRouter();

  const environmentId = get(environment, "id", "");

  const goToProcessPage = useCallback(() => {
    syncTokensToMiddleware();
    return navigateTo(`/process/${environmentId}`);
  }, [environmentId, navigateTo]);

  const meMutationFn = useCallback(() => {
    return peopleRepository.me(realm);
  }, [realm]);

  const meMutationOnSuccess = useCallback(
    async (response: Awaited<ReturnType<typeof peopleRepository.me>>) => {
      const person = get(response, "data");
      const user = await currentUserManager?.getUser();
      if (!user?.access_token || !user?.token_type) {
        throw new Error("User token information is missing");
      }
      const newUser = new User({
        ...user,
        access_token: user.access_token,
        token_type: user.token_type,
        profile: {
          preferred_username: person.identification,
        } as IdTokenClaims,
      });
      await currentUserManager?.storeUser(newUser);

      if (person.identification && environmentId && environmentUrl && realm) {
        saveTabSession({
          identification: person.identification,
          environmentId,
          environmentUrl,
          realm,
        });
      }

      goToProcessPage();
    },
    [currentUserManager, goToProcessPage, environmentId, realm, environmentUrl]
  );

  const meMutationOnError = useCallback(
    (error: AxiosError<Awaited<ReturnType<typeof peopleRepository.me>>>) => {
      handleApiError(error, true);
    },
    [handleApiError]
  );

  const meMutation = useMutation({
    mutationFn: meMutationFn,
    onSuccess: meMutationOnSuccess,
    onError: meMutationOnError,
  });

  const updateUserIdMutationFn = useCallback(() => {
    return peopleRepositoryV2.updateUserIdInKeycloakForControlledBasicProcess(environmentId);
  }, [environmentId]);

  const updateUserIdMutationOnSuccess = useCallback(async () => {
    await silentRefresh(currentUserManager);
    meMutation.mutate();
  }, [meMutation, currentUserManager, silentRefresh]);

  const updateUserIdMutationOnError = useCallback(
    (error: unknown) => {
      handleApiError(error as Error, true);
    },
    [handleApiError]
  );

  const updateUserIdMutation = useMutation({
    mutationFn: updateUserIdMutationFn,
    onSuccess: updateUserIdMutationOnSuccess,
    onError: updateUserIdMutationOnError,
  });

  const loginBasicControlledProcessMutationFn = useCallback(
    (identification: Person["identification"]) => {
      const username = identification;
      const password = environmentId;

      return authRepository.loginBasicControlledProcess({
        password,
        username,
      });
    },
    [environmentId]
  );

  const loginBasicControlledProcessOnSuccess = useCallback(
    async (response: Awaited<ReturnType<typeof authRepository.loginBasicControlledProcess>>) => {
      const tokenData = get(response, "data");
      if (!tokenData.access_token || !tokenData.token_type) {
        throw new Error("Token data is missing required fields");
      }
      const user = new User({
        ...tokenData,
        access_token: tokenData.access_token,
        token_type: tokenData.token_type,
        expires_at:
          Math.floor(Date.now() / 1000) +
          (typeof tokenData.expires_in === "number" ? tokenData.expires_in : 0),
        profile: {} as IdTokenClaims,
      });
      await currentUserManager?.storeUser(user);
      await silentRefresh(currentUserManager);
      updateUserIdMutation.mutate();
    },
    [currentUserManager, updateUserIdMutation, silentRefresh]
  );

  const loginBasicControlledProcessOnError = useCallback(
    (error: AxiosError<Awaited<ReturnType<typeof authRepository.loginBasicControlledProcess>>>) => {
      handleApiError(error, true);
    },
    [handleApiError]
  );

  const loginBasicControlledProcessMutation = useMutation({
    mutationFn: loginBasicControlledProcessMutationFn,
    onSuccess: loginBasicControlledProcessOnSuccess,
    onError: loginBasicControlledProcessOnError,
  });

  const controlledBasicProcessLogin = useCallback(
    (identification: Person["identification"]) => {
      if (!environmentId) {
        handleApiError(new Error("Environment ID is missing"), true);
        return;
      }

      loginBasicControlledProcessMutation.mutate(identification);
    },
    [environmentId, handleApiError, loginBasicControlledProcessMutation.mutate]
  );

  const controlledBasicProcessAutologin = useCallback(
    async (identification: Person["identification"]) => {
      if (!environmentId) {
        handleApiError(new Error("Environment ID is missing"), true);
        return;
      }

      if (
        identification &&
        (await isAuthenticatedWithIdentification(currentUserManager, identification))
      ) {
        updateUserIdMutation.mutate();
        return;
      }
      loginBasicControlledProcessMutation.mutate(identification);
    },
    [
      currentUserManager,
      environmentId,
      handleApiError,
      loginBasicControlledProcessMutation.mutate,
      updateUserIdMutation,
    ]
  );

  return {
    controlledBasicProcessAutologin,
    controlledBasicProcessLogin,
    controlledBasicProcessLoginLoading:
      loginBasicControlledProcessMutation.isPending ||
      meMutation.isPending ||
      updateUserIdMutation.isPending,
  };
}
